/**
 * Meta (Facebook) Pixel
 *
 * Deliberately not in index.html: the pixel belongs to the readiness campaign,
 * so it loads from /ready alone and stays off every other route of the site —
 * including an arrival by client-side navigation, which a tag in index.html
 * would miss anyway.
 *
 * No <noscript> fallback: the app is a SPA that renders nothing without
 * JavaScript, so the only hits it could ever record are crawlers.
 */

const PIXEL_ID = '1113181710531522';
const PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
  }
}

/**
 * Load the pixel and record a PageView. Safe to call more than once — the
 * script is fetched on the first call only.
 */
export const loadMetaPixel = () => {
  if (typeof window === 'undefined') return;

  if (window.fbq) {
    window.fbq('track', 'PageView');
    return;
  }

  // Meta's base code: the stub queues calls made before fbevents.js arrives,
  // and the real implementation replays them once it loads.
  const fbq: any = function (...args: any[]) {
    fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = PIXEL_SRC;
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  fbq('init', PIXEL_ID);
  fbq('track', 'PageView');

  if (process.env.NODE_ENV === 'development') {
    console.log('[MetaPixel] init', PIXEL_ID);
  }
};

/**
 * Meta's standard conversion event — this is what the ad campaign optimises
 * against, so it fires only once the lead is stored. A submit that threw is
 * not a lead, and counting it as one teaches the campaign to buy the wrong
 * people.
 *
 * No email or phone in the payload: matching those to a Meta profile needs
 * hashed values and a consent story this form does not have, and raw PII sent
 * to the pixel is discarded anyway.
 */
export const trackMetaPixelLead = (tier?: string) => {
  if (typeof window === 'undefined' || !window.fbq) return;

  const params: Record<string, string> = { content_name: 'readiness_quiz' };
  if (tier) params.content_category = tier;

  window.fbq('track', 'Lead', params);

  if (process.env.NODE_ENV === 'development') {
    console.log('[MetaPixel] Lead', params);
  }
};
